import { defineComponent as _defineComponent } from 'vue'
import { translateLang } from '@/data/i18n/translateLang'
import { sanitizeFileName } from '@/util/file/util'
import { Input, message } from 'ant-design-vue'
import { computed, nextTick, ref, watch } from 'vue'

import { categoryOptions, folderNameFillFields } from './data'
import { ROOT_ID } from './data/defaultFolderData'
import { File, Folder } from './types'
import { FolderTreeManager } from './utils/folderControl'


export default /*#__PURE__*/_defineComponent({
  __name: 'MProcessFolder',
  props: {
    folderManage: null,
    folder: null
  },
  emits: ['onOk', 'onCancel', 'update:folder'],
  setup(__props: any, { expose, emit: emits }) {

const props = __props as {
  folderManage: FolderTreeManager
  folder?: Folder
}

const isVisible = ref(false)
const model = ref<'add' | 'edit'>('add')

const folderNameInputRef = ref<InstanceType<typeof Input>>()

const FOLDER_NAME_TEMPLATE_REG = /\{[^{}]*\}/g
const isComposing = ref(false)
const composingStartValue = ref('')
const composingStartSelection = ref<{ start: number; end: number } | null>(null)
/**
 * @description 标识当前 name 变化是否由程序化操作(插入/删除)触发，
 * 程序化操作时 nameFields 已是权威数据，watch 无需重建
 */
const isProgrammaticChange = ref(false)



/** @desc 是否是根文件夹，根文件夹没有父级 */
const isRoot = computed(() => {
  return !props.folder?.pid
})
/** @desc 父级文件夹 */
const parentFolderName = computed(() => {
  const { folder, folderManage } = props
  let name = ''
  if (model.value === 'add') {
    let pid = folder?.pid
    // 不存在节点的返回空
    if (!pid) return ''
    if (folderManage.isRoot(pid)) pid = ROOT_ID
    name = folderManage.getEntityById(pid)?.name ?? ''
  } else {
    name = folder?.name ?? ''
  }
  return name ? `-(${name})` : ''
})

/**
 * @description 判断分类标签是否被选中
 * @param tagValue 标签值
 */
const isTagSelected = (tagValue: string) => {
  return folder.value.accept.classifyChains.includes(tagValue)
}


/** 文件夹对象 */
const folder = ref<Folder>({
  id: 'root',
  name: '{项目地址}',
  nameFields: [],
  nameLimitSize: 60,
  startNum: 1,
  pid: '',
  isFolder: true,
  accept: { classifyChains: [], supportedFiles: [] as File[] },
  children: [] as Folder[],
  metadata: {},
})
/** 是否显示递增序号:条件为文件夹名称是否存在递增序号选项 */
const isShowStartNum = computed(() => {
  return folder.value?.nameFields.find((v) => v.value === '{order}')
})

// 标签数据
const tagList = ref(folderNameFillFields)

// 分类方式选项
const options = ref(categoryOptions)

/**
 * @description 在光标位置插入文本到文件夹名称中，并记录插入的模板标签来源类型
 * 以 nameFields 为唯一数据源：直接在 nameFields 对应位置插入字段，再同步派生 name。
 * @param text 要插入的文本
 * @param tagType 当插入的是模板标签时，传入其来源类型（普通/分类）
 */
function insertTextAtCursor(text: string, tagType?: 'normal' | 'classify') {
  if (!folder.value) return
  const el = getFolderNameInputEl()
  if (!el) return
  const { selectionStart: start } = el
  const insertPos = start ?? 0
  const currentName = folder.value.name || ''
  // 找到光标所在片段的下标及片段内偏移量
  const { fieldIndex, offsetInField } = locateFieldByCharOffset(
    currentName,
    insertPos
  )
  // 构造新插入的字段
  const isTemplate = text.startsWith('{') && text.endsWith('}')
  const insertedField = isTemplate
    ? buildTemplateField(text, tagType)
    : { type: 'text' as const, value: text }
  // 构造新 nameFields：在 fieldIndex 位置，按 offsetInField 拆分原片段并插入
  const newNameFields = splitAndInsertField(
    folder.value.nameFields,
    fieldIndex,
    offsetInField,
    insertedField
  )
  // 同步 nameFields 与 name(name 由展示名拼接，template 字段展示 {订单号} 而非 {orderNo})
  isProgrammaticChange.value = true
  folder.value.nameFields = newNameFields
  folder.value.name = fieldsToDisplayName(newNameFields)
  isProgrammaticChange.value = false
  // 更新光标位置到插入文本末尾
  const newCursorPos = insertPos + text.length
  nextTick(() => {
    el.setSelectionRange(newCursorPos, newCursorPos)
    el.focus()
  })
}

/** @description 定位字符偏移量对应的 nameFields 下标及字段内偏移量 */
function locateFieldByCharOffset(name: string, charOffset: number) {
  const fields = folder.value?.nameFields ?? []
  let consumed = 0
  for (let i = 0; i < fields.length; i++) {
    const len = fieldDisplayText(fields[i]).length
    if (charOffset <= consumed + len) {
      return { fieldIndex: i, offsetInField: charOffset - consumed }
    }
    consumed += len
  }
  return { fieldIndex: fields.length, offsetInField: 0 }
}

/** @description 获取字段在 name 字符串中的展示文本 */
function fieldDisplayText(field: Folder['nameFields'][number]): string {
  if (field.type === 'text') return field.value
  // template 字段：value 是 {orderNo}，展示名为 {订单号}
  return getTagNameByValue(field.value) ?? field.value
}

/** @description 将 nameFields 拼接为展示用的 name 字符串 */
function fieldsToDisplayName(fields: Folder['nameFields']): string {
  return fields.map(fieldDisplayText).join('')
}

/** @description 通过 value 反查标签展示名，优先普通来源，其次分类来源 */
function getTagNameByValue(value: string): string | undefined {
  return (
    folderNameFillFields.find((v) => v.value === value)?.name ??
    categoryOptions.find((v) => v.value === value)?.name
  )
}

/** @description 构造模板字段，反查 value 与 tagType */
function buildTemplateField(
  tagName: string,
  tagType?: 'normal' | 'classify'
): { type: 'template'; value: string; tagType: 'normal' | 'classify' } {
  const v1 = folderNameFillFields.find((v) => v.name === tagName)
  const v2 = categoryOptions.find((v) => v.name === tagName)
  return {
    type: 'template',
    value: v1?.value || v2?.value || '',
    tagType: tagType ?? v2?.type ?? v1?.type ?? 'normal',
  }
}

/** @description 在指定字段位置按偏移量拆分原字段并插入新字段 */
function splitAndInsertField(
  fields: Folder['nameFields'],
  fieldIndex: number,
  offsetInField: number,
  insertedField: Folder['nameFields'][number]
): Folder['nameFields'] {
  const result: Folder['nameFields'] = []
  for (let i = 0; i < fields.length; i++) {
    const f = fields[i]
    if (i === fieldIndex) {
      // 模板字段不可拆分，整体保留后插入新字段
      if (f.type === 'template') {
        result.push(f)
        result.push(insertedField)
      } else {
        const before = f.value.slice(0, offsetInField)
        const after = f.value.slice(offsetInField)
        if (before) result.push({ ...f, value: before })
        result.push(insertedField)
        if (after) result.push({ ...f, value: after })
      }
    } else {
      result.push(f)
    }
  }
  // 光标在末尾或字段越界时，直接追加
  if (fieldIndex >= fields.length) result.push(insertedField)
  return result
}

/**
 * @description 从文件夹名称中删除第一个匹配的标签(可能会存在删错位置，但不可避免🙂)
 * @param tagName 要删除的标签名称
 */
function removeSingleTagFromName(tagName: string) {
  if (!folder.value?.name) return
  const currentName = folder.value.name
  const tagIndex = currentName.indexOf(tagName)
  if (tagIndex !== -1) {
    // 删除第一个匹配的标签
    const newName =
      currentName.slice(0, tagIndex) +
      currentName.slice(tagIndex + tagName.length)
    folder.value.name = newName
    // 更新光标位置到删除位置
    const el = getFolderNameInputEl()
    if (!el) return
    nextTick(() => {
      el.setSelectionRange(tagIndex, tagIndex)
      el.focus()
    })
  }
}

/**
 * @description 从文件夹名称中删除第一个被标记为分类来源的匹配标签
 * 以 nameFields 为唯一数据源：直接移除对应字段，再派生 name。
 * @param tagName 要删除的标签名称(展示名，如 {订单号})
 */
function removeClassifyTagFromName(tagName: string) {
  if (!folder.value?.name) return
  const nameFields = folder.value.nameFields ?? []
  // 找到第一个分类来源且展示名匹配的字段
  const removeIdx = nameFields.findIndex(
    (f) =>
      f.type === 'template' &&
      f.tagType === 'classify' &&
      fieldDisplayText(f) === tagName
  )
  if (removeIdx === -1) {
    removeSingleTagFromName(tagName)
    return
  }
  // 计算删除位置的光标(基于展示名长度)
  let cursor = 0
  for (let i = 0; i < removeIdx; i++) {
    cursor += fieldDisplayText(nameFields[i]).length
  }
  const newNameFields = nameFields.filter((_, i) => i !== removeIdx)
  // 程序化操作：nameFields 已是权威数据，watch 无需重建
  isProgrammaticChange.value = true
  folder.value.nameFields = newNameFields
  folder.value.name = fieldsToDisplayName(newNameFields)
  isProgrammaticChange.value = false
  const el = getFolderNameInputEl()
  if (!el) return
  nextTick(() => {
    el.setSelectionRange(cursor, cursor)
    el.focus()
  })
}

/**
 * @description 根据标签更新文件夹名称
 * @param tag 标签
 */
function handleInsertFolderName(tag: typeof folderNameFillFields[number]) {
  const el = getFolderNameInputEl()
  if (!el) return
  if (shouldBlockTemplateEdit(el)) {
    message.warning(translateLang('不可添加在此位置'))
    return
  }
  insertTextAtCursor(tag.name, tag.type)
}

/**
 * @description 处理文件夹名称输入，过滤非法字符
 */
function handleFolderNameInput(event: Event) {
  if (isComposing.value) return
  const target = event.target as HTMLInputElement
  if (!target) return
  const sanitizedValue = sanitizeFileName(target.value)
  if (sanitizedValue !== target.value) {
    target.value = sanitizedValue
  }
  folder.value.name = sanitizedValue
}

/**
 * @description 处理递增序号输入，仅允许数字
 */
function handleStartNumInput(event: Event) {
  const target = event.target as HTMLInputElement
  if (!target) return
  const sanitizedValue = target.value.replace(/\D/g, '')
  if (sanitizedValue !== target.value) {
    target.value = sanitizedValue
  }
  folder.value.startNum = sanitizedValue
}

/**
 * @description 禁止在花括号模板内部输入，防止误编辑
 */
function handleFolderNameBeforeInput(event: InputEvent) {
  const target = event.target as HTMLInputElement
  if (!target) return
  if (shouldBlockTemplateEdit(target)) {
    event.preventDefault()
  }
}

/**
 * @description 中文输入法开始输入
 */
function handleFolderNameCompositionStart(event: CompositionEvent) {
  isComposing.value = true
  const target = event.target as HTMLInputElement
  if (!target) return
  composingStartValue.value = target.value
  const selection = getSelectionRange(target)
  composingStartSelection.value = selection
}

/**
 * @description 中文输入法结束输入，避免在 {} 内部插入内容
 */
function handleFolderNameCompositionEnd(event: CompositionEvent) {
  isComposing.value = false
  const target = event.target as HTMLInputElement
  if (!target) return
  const startValue = composingStartValue.value
  const startSelection = composingStartSelection.value
  composingStartValue.value = ''
  composingStartSelection.value = null
  if (shouldBlockTemplateEdit(target)) {
    target.value = startValue
    folder.value.name = startValue
    if (startSelection) {
      nextTick(() => {
        target.setSelectionRange(startSelection.start, startSelection.end)
        target.focus()
      })
    }
    return
  }
  handleFolderNameInput(event as unknown as Event)
}

/** @description 获取文件夹名称输入框元素 */
function getFolderNameInputEl() {
  return folderNameInputRef.value?.$el as HTMLInputElement | undefined
}

/** @description 获取光标位置 */
function getSelectionRange(el?: HTMLInputElement | null) {
  if (!el) return null
  const { selectionStart, selectionEnd } = el
  if (selectionStart === null || selectionEnd === null) return null
  return { start: selectionStart, end: selectionEnd }
}

/** @description 判断是否阻止模板编辑 */
function shouldBlockTemplateEdit(el: HTMLInputElement) {
  const selection = getSelectionRange(el)
  if (!selection) return false
  return isSelectionBlocked(selection.start, selection.end, el.value)
}

/** @description 获取模板范围 */
function getTemplateRanges(value: string) {
  const ranges: Array<{ start: number; end: number }> = []
  let match: RegExpExecArray | null
  while ((match = FOLDER_NAME_TEMPLATE_REG.exec(value))) {
    const start = match.index
    const end = match.index + match[0].length - 1
    ranges.push({ start, end })
  }
  return ranges
}

/** @description 判断是否阻止光标位置 */
function isSelectionBlocked(
  selectionStart: number,
  selectionEnd: number,
  value: string
) {
  // 获取模版范围
  const ranges = getTemplateRanges(value)
  // 在模板范围内则阻止编辑
  return ranges.some((range) => {
    if (selectionStart === selectionEnd) {
      return selectionStart > range.start && selectionStart <= range.end
    }
    const coversWholeToken =
      selectionStart <= range.start && selectionEnd >= range.end + 1
    if (coversWholeToken) return false
    const overlaps =
      selectionStart < range.end + 1 && selectionEnd > range.start
    return overlaps
  })
}

/** @description 切换分类方式，同步更改文件名称 */
function handleClassifyChainChange(tag: typeof categoryOptions[number]) {
  const { accept } = folder.value
  const classifyChains = accept.classifyChains
  const isExist = classifyChains.includes(tag.value)
  if (isExist) {
    // 如果标签已存在，则移除
    accept.classifyChains = classifyChains.filter((item) => item !== tag.value)
    // 从文件夹名称中删除第一个匹配的分类标签
    removeClassifyTagFromName(tag.name)
  } else {
    const el = getFolderNameInputEl()
    if (el && shouldBlockTemplateEdit(el)) {
      message.warning(translateLang('不可添加在此位置'))
      return
    }
    // 如果标签不存在，在光标位置插入标签，并记录为分类来源
    insertTextAtCursor(tag.name, tag.type)
    // 将标签添加到分类链中
    accept.classifyChains.push(tag.value)
  }
}
/**
 * @description 完成处理
 */
function handleOk() {
  emits('onOk', folder.value, model.value)
}

/** 设置这次窗口打开模式 */
function setModel(type: 'add' | 'edit') {
  model.value = type
}

/** 打开弹窗 */
function open() {
  isVisible.value = true
  // 同步传入的数据
  folder.value = window.$copy(props.folder)
  // 添加弹窗打开自动激活/修改不激活
  if (model.value === 'add') {
    nextTick(() => {
      const el = folderNameInputRef.value?.$el as HTMLInputElement
      el?.focus()
      el?.select()
    })
  }
}
/** 关闭弹窗 */
function close() {
  isVisible.value = false
}
// 文件名发生改变时重新处理最终文件名的生成数据
// 仅当用户手动输入(非程序化操作)触发时，才从 name 反向重建 nameFields；
// 程序化插入/删除已直接维护 nameFields，watch 无需重建
// 使用 sync 模式：确保 isProgrammaticChange 标志在回调触发时仍然有效
watch(
  () => folder.value.name,
  (newVal) => {
    if (isProgrammaticChange.value) {
      // 程序化操作后仅同步 classifyChains 与 template 字段的一致性
      syncClassifyChains()
      return
    }
    const curlyBracketTextArr = newVal.split(/(\{[^{}]*\})/).filter(Boolean)
    folder.value.nameFields = curlyBracketTextArr.map((seg) => {
      if (seg.startsWith('{') && seg.endsWith('}')) {
        const v1 = folderNameFillFields.find((v) => v.name === seg)
        const v2 = categoryOptions.find((v) => v.name === seg)
        const value = v1?.value || v2?.value || ''
        // 用户手动输入场景下无法区分同名普通/分类来源，按数据源 type 推断
        const tagType = v2?.type ?? v1?.type
        return { type: 'template', value, tagType }
      } else {
        return { type: 'text', value: seg }
      }
    })
    syncClassifyChains()
  },
  { flush: 'sync' }
)

/** @description 同步 classifyChains 与 nameFields 中的 template 字段一致性 */
function syncClassifyChains() {
  const tempsValues = (folder.value.nameFields ?? [])
    .filter((v) => v.type === 'template')
    .map((v) => v.value)
  folder.value.accept.classifyChains = (
    folder.value.accept.classifyChains ?? []
  ).filter((v) => tempsValues.includes(v))
}

expose({
  open,
  close,
  setModel,
})

return { __sfc: true,isVisible, model, folderNameInputRef, FOLDER_NAME_TEMPLATE_REG, isComposing, composingStartValue, composingStartSelection, isProgrammaticChange, props, isRoot, parentFolderName, isTagSelected, emits, folder, isShowStartNum, tagList, options, insertTextAtCursor, locateFieldByCharOffset, fieldDisplayText, fieldsToDisplayName, getTagNameByValue, buildTemplateField, splitAndInsertField, removeSingleTagFromName, removeClassifyTagFromName, handleInsertFolderName, handleFolderNameInput, handleStartNumInput, handleFolderNameBeforeInput, handleFolderNameCompositionStart, handleFolderNameCompositionEnd, getFolderNameInputEl, getSelectionRange, shouldBlockTemplateEdit, getTemplateRanges, isSelectionBlocked, handleClassifyChainChange, handleOk, setModel, open, close, syncClassifyChains }
}

})